import { parseUnits, zeroAddress } from 'viem'

export type Currency = 'bnb' | 'cbon'
export const CURRENCY_INDEX: Record<Currency, 0 | 1> = { bnb: 0, cbon: 1 }
export type VoucherKind = 'percent_off' | 'amount_off' | 'fixed_price'
export const KIND_INDEX: Record<VoucherKind, 0 | 1 | 2> = { percent_off: 0, amount_off: 1, fixed_price: 2 }

/** Same rule as CadinuTradingCompetitionV4.applyVoucher: never negative, never above the input price. */
export function applyVoucher(price: bigint, kind: VoucherKind, value: bigint): bigint {
  if (kind === 'percent_off') return value >= 10000n ? 0n : (price * (10000n - value)) / 10000n
  if (kind === 'amount_off') return value >= price ? 0n : price - value
  return value < price ? value : price
}

export type Breakdown = { list: bigint; referralDiscount: bigint; promoDiscount: bigint; total: bigint; referrerShareMax: bigint }

/**
 * The fee in the contract's fixed order: list price -> referral price (when a referrer is given) -> promo voucher.
 * `referrerShareMax` is the most the referrer can get (60 % cap) — the exact share depends on the referrer's tier.
 */
export function feeBreakdown(cost: bigint, costWithReferral: bigint, withReferrer: boolean, promo?: { kind: VoucherKind; value: bigint }): Breakdown {
  const afterRef = withReferrer ? costWithReferral : cost
  const total = promo ? applyVoucher(afterRef, promo.kind, promo.value) : afterRef
  return { list: cost, referralDiscount: cost - afterRef, promoDiscount: afterRef - total, total, referrerShareMax: withReferrer ? (total * 6000n) / 10000n : 0n }
}

export type SignedVoucher = {
  voucher: { creator: `0x${string}`; currency: number; kind: number; value: string; maxUses: number; expiry: number; nonce: `0x${string}`; campaign: `0x${string}`; campaignCap: number }
  signature: `0x${string}`
}

export type V4CreateInput = {
  startSec: number
  days: number
  pool: `0x${string}`
  projectToken: `0x${string}`
  winners: number
  amount: string
  decimals: number
  rewardToken: `0x${string}`
  type: number
  configId: bigint
  rulesHash: `0x${string}`
  currency: Currency
  expectedFee: bigint
  referrer?: `0x${string}`
  voucher?: SignedVoucher
}

const ZERO32 = `0x${'0'.repeat(64)}` as `0x${string}`

/** Arguments of CadinuTradingCompetitionV4.startCompetition(CreateParams, FeeParams) and the BNB value to attach. */
export function buildStartCompetitionV4(i: V4CreateInput) {
  const start = BigInt(Math.floor(i.startSec))
  const p = {
    startTime: start,
    endTime: start + BigInt(Math.round(i.days * 86400)),
    poolAddress: i.pool,
    projectToken: i.projectToken,
    numberOfWinners: BigInt(i.winners),
    rewardAmount: parseUnits(i.amount, i.decimals),
    rewardToken: i.rewardToken,
    competitionTypeIndex: BigInt(i.type),
    expectedValidatorConfigId: i.configId,
    expectedRulesHash: i.rulesHash,
  }
  const v = i.voucher?.voucher
  const f = {
    currency: CURRENCY_INDEX[i.currency],
    expectedFee: i.expectedFee,
    referrer: i.referrer ?? zeroAddress,
    useVoucher: Boolean(v),
    voucher: v
      ? { creator: v.creator, currency: v.currency, kind: v.kind, value: BigInt(v.value), maxUses: v.maxUses, expiry: BigInt(v.expiry), nonce: v.nonce, campaign: v.campaign, campaignCap: v.campaignCap }
      : { creator: zeroAddress, currency: 0, kind: 0, value: 0n, maxUses: 0, expiry: 0n, nonce: ZERO32, campaign: ZERO32, campaignCap: 0 },
    voucherSignature: (i.voucher?.signature ?? '0x') as `0x${string}`,
  }
  return { args: [p, f] as const, value: i.currency === 'bnb' ? i.expectedFee : 0n }
}
